import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './AdminShared.module.scss';
import {
    AdminEventCreateDto,
    AdminEventDto,
    AdminLocationDto,
    TagDto
} from '../../../types/admin.types';
import {
    archiveAdminEvent,
    createAdminTag,
    createAdminLocation,
    createAdminEvent,
    fetchAdminEvents,
    fetchAdminLocations,
    fetchAdminTags,
    restoreAdminEvent,
    updateAdminEvent
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import ImagePickerField from './components/ImagePickerField';
import { toDateTimeLocalInput, toUtcIsoString } from '../../../utils/date-time';
import EventFormFields, {
    emptyEventForm,
    normalizeEventCreatePayload
} from './components/EventFormFields';
import VenueFormFields, {
    emptyVenueForm,
    normalizeVenueCreatePayload
} from './components/VenueFormFields';

const normalizeName = (value?: string | null) => (value ?? '').trim().toLowerCase();

const mapEvent = (item: any): AdminEventDto => ({
    id: item.id ?? item.Id ?? '',
    name: item.name ?? item.Name ?? '',
    description: item.description ?? item.Description ?? null,
    eventDate: item.eventDate ?? item.EventDate ?? '',
    eventUrl: item.eventUrl ?? item.EventUrl ?? null,
    thumbnailUrl: item.thumbnailUrl ?? item.ThumbnailUrl ?? null,
    locationId: item.locationId ?? item.LocationId ?? null,
    locationName: item.locationName ?? item.LocationName ?? null,
    tags: item.tags ?? item.Tags ?? [],
    hideFromFightCards: item.hideFromFightCards ?? item.HideFromFightCards ?? false,
    isArchived: item.isArchived ?? item.IsArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? ''
});

const mapLocation = (item: any): AdminLocationDto => ({
    id: item.id ?? item.Id ?? '',
    name: item.name ?? item.Name ?? '',
    city: item.city ?? item.City ?? null,
    region: item.region ?? item.Region ?? null,
    country: item.country ?? item.Country ?? null,
    venueUrl: item.venueUrl ?? item.VenueUrl ?? null,
    isArchived: item.isArchived ?? item.IsArchived ?? false,
    createdDate: item.createdDate ?? item.CreatedDate ?? '',
    updatedDate: item.updatedDate ?? item.UpdatedDate ?? ''
});

const AdminEvents = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [searchParams] = useSearchParams();
    const [events, setEvents] = useState<AdminEventDto[]>([]);
    const [locations, setLocations] = useState<AdminLocationDto[]>([]);
    const [tags, setTags] = useState<TagDto[]>([]);
    const [showArchived, setShowArchived] = useState(false);
    const [form, setForm] = useState<AdminEventCreateDto>(emptyEventForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [tagInput, setTagInput] = useState('');
    const [pendingEventId, setPendingEventId] = useState<string | null>(null);
    const [locationSearchInput, setLocationSearchInput] = useState('');
    const [isCreateVenueModalOpen, setIsCreateVenueModalOpen] = useState(false);
    const [createVenueForm, setCreateVenueForm] = useState(emptyVenueForm);
    const [isCreatingVenue, setIsCreatingVenue] = useState(false);
    const [createVenueError, setCreateVenueError] = useState<string | null>(null);

    const activeLocations = useMemo(
        () => locations.filter((location) => !location.isArchived),
        [locations]
    );

    const activeLocationOptions = useMemo(
        () => activeLocations.map((location) => ({
            value: location.id,
            label: location.name,
            subLabel: [location.city, location.region, location.country].filter(Boolean).join(', ')
        })),
        [activeLocations]
    );

    const availableTags = useMemo(
        () => tags
            .map((tag) => tag.name)
            .filter((tagName) => !form.tags.some((value) => normalizeName(value) === normalizeName(tagName))),
        [form.tags, tags]
    );

    useEffect(() => {
        const eventId = searchParams.get('eventId');
        setPendingEventId(eventId);
    }, [searchParams]);

    useEffect(() => {
        if (!pendingEventId) {
            return;
        }

        const matched = events.find((item) => item.id === pendingEventId);
        if (matched) {
            setSelectedId(matched.id);
            setForm({
                name: matched.name,
                description: matched.description ?? '',
                eventDate: toDateTimeLocalInput(matched.eventDate),
                eventUrl: matched.eventUrl ?? '',
                thumbnailUrl: matched.thumbnailUrl ?? '',
                locationId: matched.locationId ?? '',
                tags: matched.tags ?? [],
                hideFromFightCards: matched.hideFromFightCards ?? false
            });
            setLocationSearchInput(matched.locationName ?? '');
            setTagInput('');
        }
    }, [events, pendingEventId]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const [eventsResponse, locationsResponse, tagsResponse] = await Promise.all([
                fetchAdminEvents(showArchived, token),
                fetchAdminLocations(true, token),
                fetchAdminTags(token)
            ]);
            setEvents((eventsResponse ?? []).map((item: any) => mapEvent(item)));
            setLocations((locationsResponse ?? []).map((item: any) => mapLocation(item)));
            setTags((tagsResponse ?? []).map((item: any) => ({
                id: item.id ?? item.Id ?? '',
                name: item.name ?? item.Name ?? '',
                usageCount: item.usageCount ?? item.UsageCount ?? 0,
                lastUsedDate: item.lastUsedDate ?? item.LastUsedDate ?? null
            })));
        } catch (error) {
            setErrorMessage('Unable to load events.');
        } finally {
            setIsLoading(false);
        }
    }, [showArchived, token]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const resetForm = () => {
        setForm(emptyEventForm);
        setSelectedId(null);
        setLocationSearchInput('');
        setTagInput('');
    };

    const handleEdit = (item: AdminEventDto) => {
        setSelectedId(item.id);
        setForm({
            name: item.name,
            description: item.description ?? '',
            eventDate: toDateTimeLocalInput(item.eventDate),
            eventUrl: item.eventUrl ?? '',
            thumbnailUrl: item.thumbnailUrl ?? '',
            locationId: item.locationId ?? '',
            tags: item.tags ?? [],
            hideFromFightCards: item.hideFromFightCards ?? false
        });
        setLocationSearchInput(item.locationName ?? '');
        setTagInput('');
    };

    const addExistingTagByName = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return;
        }

        const normalized = normalizeName(trimmed);
        if (form.tags.some((tag) => normalizeName(tag) === normalized)) {
            return;
        }

        const existing = tags.find((tag) => normalizeName(tag?.name) === normalized);
        if (!existing) {
            return;
        }

        setForm({ ...form, tags: [...form.tags, existing.name] });
    };

    const removeTag = (value: string) => {
        setForm({
            ...form,
            tags: form.tags.filter((tag) => tag !== value)
        });
    };

    const createTag = async () => {
        const trimmed = tagInput.trim();
        if (!trimmed) {
            return;
        }

        const normalized = normalizeName(trimmed);
        if (form.tags.some((tag) => normalizeName(tag) === normalized)) {
            setTagInput('');
            return;
        }

        const existing = tags.find((tag) => normalizeName(tag?.name) === normalized);
        if (existing) {
            setForm({ ...form, tags: [...form.tags, existing.name] });
            setTagInput('');
            return;
        }

        try {
            const created = await createAdminTag({ name: trimmed }, token);
            const createdName = created?.name ?? trimmed;
            const normalizedCreated: TagDto = {
                id: created?.id ?? '',
                name: createdName,
                usageCount: created?.usageCount ?? 0,
                lastUsedDate: created?.lastUsedDate ?? null
            };

            setTags((prev) => [...prev, normalizedCreated]);
            setForm({ ...form, tags: [...form.tags, createdName] });
            setTagInput('');
        } catch (error) {
            setErrorMessage('Unable to create tag.');
        }
    };

    const handleOpenCreateVenueModal = () => {
        setCreateVenueForm({
            ...emptyVenueForm,
            name: locationSearchInput.trim().slice(0, 200)
        });
        setCreateVenueError(null);
        setIsCreateVenueModalOpen(true);
    };

    const handleCloseCreateVenueModal = () => {
        setIsCreateVenueModalOpen(false);
        setCreateVenueForm(emptyVenueForm);
        setCreateVenueError(null);
    };

    const handleCreateVenueFromModal = async (event: React.FormEvent) => {
        event.preventDefault();

        const payload = normalizeVenueCreatePayload(createVenueForm);
        if (!payload.name) {
            setCreateVenueError('Venue name is required.');
            return;
        }

        setIsCreatingVenue(true);
        setCreateVenueError(null);
        setErrorMessage(null);

        try {
            const createdLocation = mapLocation(await createAdminLocation(payload, token));
            const locationsResponse = await fetchAdminLocations(true, token);
            const refreshedLocations = (locationsResponse ?? []).map((item: any) => mapLocation(item));
            setLocations(refreshedLocations);
            const selectedLocation = refreshedLocations.find((item) => item.id === createdLocation.id)
                ?? refreshedLocations.find((item) => normalizeName(item.name) === normalizeName(payload.name));

            if (!selectedLocation?.id) {
                setCreateVenueError('Venue created, but could not auto-select it. Please choose it from the venue list.');
                return;
            }

            setForm((previous) => ({
                ...previous,
                locationId: selectedLocation.id
            }));
            setLocationSearchInput(selectedLocation.name);
            handleCloseCreateVenueModal();
        } catch (error) {
            setCreateVenueError(error instanceof Error ? error.message : 'Unable to create venue.');
        } finally {
            setIsCreatingVenue(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);

        const normalized = normalizeEventCreatePayload(form);
        const payload: AdminEventCreateDto = {
            ...normalized,
            eventDate: toUtcIsoString(normalized.eventDate) ?? ''
        };

        if (!payload.eventDate) {
            setErrorMessage('Event date is required.');
            return;
        }

        try {
            if (selectedId) {
                await updateAdminEvent(selectedId, payload, token);
            } else {
                await createAdminEvent(payload, token);
            }
            await loadData();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save event.');
        }
    };

    const handleArchiveToggle = async (item: AdminEventDto) => {
        setErrorMessage(null);
        try {
            if (item.isArchived) {
                await restoreAdminEvent(item.id, token);
            } else {
                await archiveAdminEvent(item.id, token);
            }

            if (selectedId === item.id) {
                resetForm();
            }

            await loadData();
        } catch (error) {
            setErrorMessage('Unable to update archive status.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Events</h1>
                    <p>Manage fight card events and venue links.</p>
                </div>
                <div className={styles.actions}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        {' '}Show archived
                    </label>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Event List</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading events...</div>
                    ) : events.length === 0 ? (
                        <div className={styles.empty}>No events found.</div>
                    ) : (
                        events.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.name}</strong>
                                    <div className={styles.muted}>
                                        {new Date(item.eventDate).toLocaleString()} • {item.locationName ?? 'No venue'}
                                        {item.hideFromFightCards ? ' • Hidden from Fight Cards' : ''}
                                        {item.isArchived ? ' • Archived' : ''}
                                    </div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleArchiveToggle(item)}>
                                        {item.isArchived ? 'Restore' : 'Archive'}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Event' : 'New Event'}</h2>
                    <form onSubmit={handleSubmit}>
                        <EventFormFields
                            form={form}
                            onChange={setForm}
                            fieldClassName={styles.field}
                            actionsClassName={styles.actions}
                            secondaryButtonClassName={styles.secondary}
                            locationOptions={activeLocationOptions}
                            onLocationSearchChange={setLocationSearchInput}
                            onCreateVenueClick={handleOpenCreateVenueModal}
                            showCreateVenueButton
                            idPrefix="event"
                        />
                        <ImagePickerField
                            label="Image URL"
                            value={form.thumbnailUrl ?? ''}
                            onChange={(next) => setForm({ ...form, thumbnailUrl: next })}
                            token={token}
                            helperText="Pick from the image library or paste an external URL."
                        />
                        <div className={styles.field}>
                            <label>Tags</label>
                            <div className={styles.actions}>
                                <input
                                    value={tagInput}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setTagInput(value);
                                        addExistingTagByName(value);
                                    }}
                                    list="event-tag-list"
                                    placeholder="Search existing tags"
                                    maxLength={200}
                                />
                                <datalist id="event-tag-list">
                                    {availableTags.map((tag) => (
                                        <option key={tag} value={tag} />
                                    ))}
                                </datalist>
                                <button type="button" className={styles.secondary} onClick={createTag}>
                                    Create new
                                </button>
                            </div>
                            <div className={styles.actions}>
                                {form.tags.map((tag) => (
                                    <button key={tag} type="button" className={styles.secondary} onClick={() => removeTag(tag)}>
                                        #{tag} x
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Event' : 'Create Event'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
            {isCreateVenueModalOpen ? (
                <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="event-create-venue-title">
                    <div className={styles.modalBackdrop} onClick={handleCloseCreateVenueModal} />
                    <div className={styles.modalPanel}>
                        <div className={styles.header}>
                            <div>
                                <h2 id="event-create-venue-title">Create Venue</h2>
                                <p>Add a venue without leaving the event editor.</p>
                            </div>
                        </div>
                        <form onSubmit={handleCreateVenueFromModal}>
                            {createVenueError ? <div className={styles.muted}>{createVenueError}</div> : null}
                            <VenueFormFields
                                form={createVenueForm}
                                onChange={setCreateVenueForm}
                                fieldClassName={styles.field}
                                idPrefix="event-create-venue"
                            />
                            <div className={styles.actions}>
                                <button type="submit" className={styles.primary} disabled={isCreatingVenue}>
                                    {isCreatingVenue ? 'Creating...' : 'Create and Select'}
                                </button>
                                <button type="button" className={styles.secondary} onClick={handleCloseCreateVenueModal} disabled={isCreatingVenue}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default AdminEvents;
